import { Role } from '@/enums/role.enum'

import { UserProfile } from '@/types/user.type'

/**
 * Check if a user has admin role
 * @param user - User profile object
 * @returns boolean - true if user is admin
 */
export function isAdmin(user?: UserProfile): boolean {
  if (!user || !user.roles) return false
  return user.roles.includes(Role.ADMIN)
}

/**
 * Check if a user has instructor role
 * @param user - User profile object
 * @returns boolean - true if user is instructor
 */
export function isInstructor(user?: UserProfile): boolean {
  if (!user || !user.roles) return false
  return user.roles.includes(Role.INSTRUCTOR)
}

/**
 * Check if a user has any admin-related role (admin, instructor, teaching assistant)
 * @param user - User profile object
 * @returns boolean - true if user has admin-related role
 */
export function hasAdminAccess(user?: UserProfile): boolean {
  if (!user || !user.roles) return false
  return user.roles.some(role => role === Role.ADMIN || role === Role.INSTRUCTOR || role === Role.TEACHING_ASSISTANT)
}

/**
 * Check if a user can access premium content
 * This includes:
 * - Users who have paid for the course
 * - Admin users
 * - Instructors
 * - Users with preview access
 * @param user - User profile object
 * @param hasPaidAccess - Whether user has paid access to the course
 * @param isPreview - Whether the content is preview/free
 * @returns boolean - true if user can access premium content
 */
export function canAccessPremiumContent(user?: UserProfile, hasPaidAccess?: boolean, isPreview?: boolean): boolean {
  // Preview content is always accessible
  if (isPreview) return true

  // Paid users have access
  if (hasPaidAccess) return true

  // Admin users have access to everything
  if (isAdmin(user)) return true

  // Instructors have access to their courses
  if (isInstructor(user)) return true

  return false
}
